import { PdfGen, PdfUnit, PdfSize, PdfRect, PdfPoint, PdfBrush, PageGen } from '../../pdf/pdf';
import { ILayoutLabelGen } from '../layoutLabel';
import { IItemCount } from '@educraft/base';
import { OptsLabel } from '../optsLabel';
import { GUtil } from '../../pdf/gutil';
import '../../pdf/draw/primitives';
import '../../pdf/draw/label';
import '../../pdf/draw/transforms';

function calc(
  p: PdfPoint,
  v: { x: number; y: number },
  ...steps: [scale: number, angleDeg: number][]
): PdfPoint {
  let px = p.x,
    py = p.y;
  for (const [scale, angleDeg] of steps) {
    const vx = v.x * scale;
    const vy = v.y * scale;
    const rad = (angleDeg * Math.PI) / 180;
    px += vx * Math.cos(rad) - vy * Math.sin(rad);
    py += vx * Math.sin(rad) + vy * Math.cos(rad);
  }
  return new PdfPoint(px, py);
}

function vec(p0: PdfPoint, p1: PdfPoint): { x: number; y: number } {
  return { x: p1.x - p0.x, y: p1.y - p0.y };
}

/**
 * Trimino — Triangle-based puzzle matching worksheet (Page 1 only).
 *
 * Layout: 1 portrait page with 9 interlocking triangles; each triangle edge
 * shows either an image or its text label (alternating per item, random start).
 *
 * Blue puzzle-connector "stripe" and "corner" decorations mark the edge
 * midpoints for easy cut-and-reassemble.
 *
 * Migrated from C# EduFrame.Img10.Generators.Trimino
 */
export class Trimino implements ILayoutLabelGen, IItemCount {
  countDefault(): number {
    return 9;
  }
  countMin(): number {
    return 9;
  }
  countMax(): number {
    return 9;
  }

  public async generate(pdf: PdfGen, opts: OptsLabel): Promise<void> {
    const rnd = opts.data.random;

    const margin = PdfUnit.FromMillimeters(5);
    const size1 = (PdfSize.A4.width - 2 * margin) / 2;
    const size2 = (((PdfSize.A4.height - 2 * margin) / 3) * 2) / Math.sqrt(3);
    const minSize = Math.min(size1, size2);
    const sizeW = minSize;
    const sizeH = (minSize * Math.sqrt(3)) / 2;

    const imgW = PdfUnit.FromMillimeters(25);
    const imgH = PdfUnit.FromMillimeters(25);

    const switches = Array.from({ length: 9 }, () => rnd.next(0, 2) === 0);

    const page = pdf.addPage();

    const drawTriangle = (gx: number, gy: number, top: boolean, ...indexes: number[]) => {
      const p1 = new PdfPoint(margin + gx * sizeW, margin + gy * sizeH);
      const p2 = new PdfPoint(margin + (gx + 1) * sizeW, margin + gy * sizeH);

      let list: PdfPoint[];
      if (top) {
        const p3 = new PdfPoint(margin + (gx + 0.5) * sizeW, margin + (gy - 1) * sizeH);
        list = [p1, p2, p3];
      } else {
        const p3 = new PdfPoint(margin + (gx + 0.5) * sizeW, margin + (gy + 1) * sizeH);
        list = [p1, p3, p2];
      }

      const pts = [...list, list[0]];

      for (let i = 0; i < list.length; i++) {
        const v = vec(pts[i], pts[i + 1]);

        const stripe: PdfPoint[] = [
          pts[i],
          pts[i + 1],
          calc(pts[i + 1], v, [0.03, -120]),
          calc(pts[i], v, [0.03, -60]),
        ];
        page.drawPolygon(null, PdfBrush.LightSkyBlue, stripe);

        const corner: PdfPoint[] = [
          pts[i],
          calc(pts[i], v, [0.15, 0]),
          calc(pts[i], v, [0.15, -60]),
        ];
        page.drawPolygon(null, PdfBrush.LightSkyBlue, corner);
      }

      for (let i = 0; i < list.length; i++) {
        const v = vec(pts[i], pts[i + 1]);
        const pp = calc(pts[i], v, [0.5, 0], [0.15, -90]);

        drawLabel(page, pp, v, indexes[i]);

        page.drawLine(GUtil.basePen, pts[i], pts[i + 1]);
      }
    };

    const drawLabel = (pg: PageGen, p: PdfPoint, v: { x: number; y: number }, idx: number) => {
      if (idx === 0) return;
      const index = idx - 1;
      if (index >= opts.list.length) return;

      const angleDeg = (-Math.atan2(v.y, v.x) * 180) / Math.PI;
      const r1 = new PdfRect(p.x - imgW / 2, p.y - imgH / 2, imgW, imgH);

      pg.save();
      pg.rotateAtTransform(angleDeg, p);

      const label = switches[index] ? opts.list[index].left : opts.list[index].right;
      pg.drawLabel(label, { rect: r1 });

      pg.restore();

      switches[index] = !switches[index];
    };

    drawTriangle(0, 0, false, 1, 0, 0);
    drawTriangle(0.5, 1, true, 4, 2, 0);
    drawTriangle(1, 0, false, 2, 3, 1);

    drawTriangle(0, 2, true, 3, 5, 0);
    drawTriangle(0.5, 1, false, 6, 0, 0);
    drawTriangle(1, 2, true, 6, 7, 4);

    drawTriangle(0, 2, false, 8, 7, 0);
    drawTriangle(0.5, 3, true, 5, 8, 9);
    drawTriangle(1, 2, false, 9, 0, 0);
  }
}
